import { Button, ButtonProps } from "@mantine/core";
import { useTheme } from "@contexts/ThemeContext";
import { ReactNode } from "react";

export interface JarvisFloatingButtonProps extends Omit<ButtonProps, 'leftSection' | 'rightSection' | 'onClick'> {
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onClick?: () => void | Promise<void>;
}

/**
 * JarvisFloatingButton - A themed floating Button component that automatically applies
 * the current theme colors with outline variant and shadow effects.
 *
 * Usage: Use this for floating action buttons (edit items, quick actions, etc.)
 *
 * Props:
 * - leftIcon: ReactNode - Icon to display on the left side of the button
 * - rightIcon: ReactNode - Icon to display on the right side of the button
 * - All other Mantine Button props are supported
 */
export const JarvisFloatingButton = ({ leftIcon, rightIcon, ...props }: JarvisFloatingButtonProps) => {
  const { colors: themeColors } = useTheme();

  return (
    <Button
      {...props}
      variant="outline"
      leftSection={leftIcon}
      rightSection={rightIcon}
      styles={{
        root: {
          backgroundColor: "transparent",
          color: themeColors.primary,
          borderColor: themeColors.primary,
          borderRadius: '10px',
          boxShadow: `0 4px 20px ${themeColors.primary}26`,
          '&:hover': {
            backgroundColor: `${themeColors.primary}1A`,
            borderColor: themeColors.primaryHover,
            color: themeColors.primaryHover,
            boxShadow: `0 8px 30px ${themeColors.primary}40`,
          },
          '&:disabled': {
            backgroundColor: 'transparent',
            borderColor: themeColors.border,
            color: themeColors.textSecondary,
            opacity: 0.5,
          }
        },
        label: {
          color: themeColors.primary,
        },
        section: {
          color: themeColors.primary,
        }
      }}
    />
  );
};

export default JarvisFloatingButton;
